import 'react-day-picker/style.css';

import Popover from '@mui/material/Popover';
import { styled } from '@mui/system';
import { DateInput } from '@src/common/components/dates/DateInput';
import { usePopover } from '@src/common/components/dates/hooks/usePopover';
import type { TextInput } from '@src/common/components/TextInput';
import { theme } from '@src/common/styles/Theme';
import { Timestamp } from '@src/common/time/Timestamp';
import { isValid, parseISO } from 'date-fns';
import type { ComponentProps, ForwardedRef } from 'react';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { DayPicker, TZDate } from 'react-day-picker';
import { useTranslation } from 'react-i18next';

export const DatePicker = forwardRef(function CustomInput(
  {
    onChange,
    onBlur,
    value = null,
    isUTC,
    endAdornment,
    ...props
  }: Omit<ComponentProps<typeof TextInput>, 'onChange' | 'onBlur' | 'value'> & {
    onChange: (newDate: Date | null) => void;
    endAdornment?: JSX.Element;
    value?: Date | string | null;
    onBlur?: () => void;
    isUTC?: boolean;
  },
  ref: ForwardedRef<HTMLDivElement>,
) {
  const inputRef = useRef<HTMLDivElement | null>(null);
  useImperativeHandle(ref, () => inputRef.current!, []);

  const { t } = useTranslation();
  const { anchorElement, openPopover, closePopover, isOpen } = usePopover();

  const toValidDate = (value: Date | string | null): Date | null => {
    if (value instanceof Date && !isNaN(value.getTime())) {
      return value;
    }
    if (typeof value === 'string') {
      const parsedDate = parseISO(value);
      if (isValid(parsedDate)) {
        return parsedDate;
      }
    }
    return null;
  };

  const formatDate = (value: Date | string | null): string => {
    const validDate = toValidDate(value);
    if (!validDate) {
      return '';
    }

    return isUTC
      ? new Timestamp(validDate).formatUTCDate()
      : new Timestamp(validDate).formatDate();
  };

  type DatePresentation = {
    inputValue: string;
    pickerValue: Date | null;
  };

  const validInitialValue = toValidDate(value);
  const [date, setDate] = useState<DatePresentation>({
    inputValue: formatDate(value),
    pickerValue: validInitialValue,
  });

  useEffect(() => {
    if (value === null) {
      setDate({
        inputValue: '',
        pickerValue: null,
      });
    }
  }, [value]);

  const handleDayPickerSelect = (selectedDate: Date | null) => {
    setDate({
      inputValue: formatDate(selectedDate),
      pickerValue: selectedDate,
    });
    onChange(selectedDate);
    closePopover();
  };

  const handleInputChange = (newValue: string) => {
    const parsedDate = isUTC
      ? Timestamp.tryUTCFromDDMMYYYY(newValue)?.getDate()
      : Timestamp.tryFromDDMMYYYY(newValue)?.getDate();

    const newDate = parsedDate || null;
    setDate({
      inputValue: newValue,
      pickerValue: newDate,
    });
    onChange(newDate);
  };

  const handleInputBlur = () => {
    setDate({
      inputValue: formatDate(date.pickerValue),
      pickerValue: date.pickerValue,
    });
    onBlur?.();
  };

  const handleCalendarIconClick = () => openPopover(inputRef.current);

  const defaultMonth = isUTC
    ? date.pickerValue
      ? new TZDate(date.pickerValue, 'UTC')
      : new TZDate(new Date(), 'UTC')
    : date.pickerValue || new Date();

  const selected = isUTC
    ? date.pickerValue
      ? new TZDate(date.pickerValue, 'UTC')
      : undefined
    : date.pickerValue || undefined;

  return (
    <>
      <DateInput
        {...props}
        inputRef={inputRef}
        value={date.inputValue}
        onChange={(e) => handleInputChange(e.target.value)}
        onBlur={handleInputBlur}
        onCalendarClick={handleCalendarIconClick}
        endAdornment={endAdornment}
        placeholder={t('datePlaceholder')}
      />
      <Popover
        open={isOpen}
        anchorEl={anchorElement}
        onClose={closePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <StyledDayPicker
          timeZone={isUTC ? 'UTC' : undefined}
          defaultMonth={defaultMonth}
          mode="single"
          selected={selected}
          onSelect={(selectedDate) =>
            handleDayPickerSelect(selectedDate || null)
          }
        />
      </Popover>
    </>
  );
});

const StyledDayPicker = styled(DayPicker)`
  padding: ${theme.spacing['2']};
  font-family: 'IBM Plex Sans', sans-serif;
  background-color: ${theme.palette.surface.surfaceContainer};
  --rdp-accent-color: ${theme.palette.primary.primary} !important;
  --rdp-today-color: ${theme.palette.primary.inversePrimary} !important;
`;
